/*
 * aura-request-form.js: today's request forms, native in the theme (sections/request-form.liquid).
 *
 * <aura-request-form> posts the form as JSON to the Momentum API under the field names Momentum reads
 * (swatch chips /aura/swatchRequestNew, shop sign-up /aura/shopRequest, Wrap Academy /aura/academySignup),
 * then records the person at the Marketing API's /events/user and keeps its id as `mkt`, as formpage.js
 * did. Wrap Academy also sends the email to /events/classSignup.
 * Swatch chips: the color fields suggest every color in the 3D explorer, ?color= fills Color 1, and a
 * device can send one request every 7 days. The lock uses formpage.js's storage key, so locks set by
 * today's theme carry over; ?clearSwatch clears it.
 */
(() => {
  const PATHS = { swatch: '/aura/swatchRequestNew', shop: '/aura/shopRequest', academy: '/aura/academySignup' };
  const LOCK_KEY = 'swatch_form_lockout';
  const LOCK_MS = 7 * 24 * 60 * 60 * 1000;
  const DOMAIN_TYPOS = {
    'gmai.com': 'gmail.com', 'gmial.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gmailcom': 'gmail.com',
    'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yhoo.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmal.com': 'hotmail.com', 'homail.com': 'hotmail.com',
    'outllok.com': 'outlook.com', 'otlook.com': 'outlook.com'
  };

  const track = (name, props) => window.aura && window.aura.track(name, props);
  const apiBase = (url) => String(url || '').trim().replace(/\/+$/, '');
  /* Locale strings from the section's JSON, decoded back to plain text (the `t` filter HTML-escapes);
     they are only ever set with textContent */
  const strings = (root, selector) => {
    let raw = {};
    try { raw = JSON.parse(root.querySelector(selector).textContent); } catch (e) { return raw; }
    const decoder = document.createElement('textarea');
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => { decoder.innerHTML = v; return [k, decoder.value]; }));
  };
  const postJSON = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  /* The domain typos formpage.js fixed (gmai.com, yahooo.com), plus a .con, .comm or .comu ending */
  function fixEmail(email) {
    const at = email.lastIndexOf('@');
    if (at < 1) return email;
    const domain = email.slice(at + 1).toLowerCase();
    return `${email.slice(0, at)}@${DOMAIN_TYPOS[domain] || domain.replace(/\.(con|comm|comu)$/, '.com')}`;
  }

  function swatchLocked() {
    try {
      if (location.search.indexOf('clearSwatch') !== -1) { localStorage.removeItem(LOCK_KEY); return false; }
      const time = Number(localStorage.getItem(LOCK_KEY));
      if (time > 0 && Date.now() - time < LOCK_MS) return true;
      localStorage.removeItem(LOCK_KEY);
    } catch (e) { /* storage blocked: never lock */ }
    return false;
  }

  class AuraRequestForm extends HTMLElement {
    connectedCallback() {
      this.kind = PATHS[this.dataset.form] ? this.dataset.form : 'swatch';
      this.t = strings(this, '[data-request-strings]');
      this.form = this.querySelector('[data-request-form]');
      this.submitBtn = this.querySelector('[data-request-submit]');
      this.errorEl = this.querySelector('[data-request-error]');
      this.form.addEventListener('submit', (e) => this.submit(e));
      this.form.addEventListener('input', (e) => {
        if (e.target.getAttribute('aria-invalid') === 'true') this.setFieldError(e.target, '');
      });
      if (this.kind !== 'swatch') return;
      const color = this.field('color1');
      const fromPage = new URLSearchParams(location.search).get('color');
      if (fromPage && !color.value) color.value = fromPage.slice(0, 60);
      if (swatchLocked()) this.lock();
      else this.loadColors();
    }

    field(name) {
      return this.form.elements.namedItem(name);
    }

    /* Suggestions only: free text still sends if the list fails to load */
    async loadColors() {
      const list = this.querySelector('datalist');
      const explorer = apiBase(this.dataset.explorer);
      if (!list || !explorer) return;
      try {
        const res = await fetch(`${explorer}/search-explorer?s=&v2=1`);
        const { array } = await res.json();
        const names = [...new Set((array || []).map((c) => c && c.color).filter(Boolean))].sort();
        list.replaceChildren(...names.map((name) => { const option = document.createElement('option'); option.value = name; return option; }));
      } catch (e) { /* keep free text */ }
    }

    lock() {
      this.querySelector('[data-request-locked]').hidden = false;
      this.form.classList.add('is-locked');
      Array.from(this.form.elements).forEach((el) => { el.disabled = true; });
    }

    setFieldError(input, message) {
      const error = document.getElementById(`${input.id}-error`);
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
    }

    validate() {
      let first = null;
      this.form.querySelectorAll('[data-request-field]').forEach((input) => {
        const value = input.value.trim();
        const digits = value.replace(/\D/g, '').length;
        let message = '';
        if (input.required && !value) message = this.t.required;
        else if (value && input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) message = this.t.invalid_email;
        else if (value && input.type === 'tel' && (digits < 10 || digits > 15)) message = this.t.invalid_phone;
        this.setFieldError(input, message);
        if (message && !first) first = input;
      });
      return first;
    }

    async submit(e) {
      e.preventDefault();
      if (this.submitBtn.getAttribute('aria-busy') === 'true') return;
      // Honeypot: people never see this field, bots fill it. Look sent, send nothing
      if (this.field('company_site').value) { this.showSent(); return; }
      const invalid = this.validate();
      if (invalid) { invalid.focus(); return; }

      const data = {};
      this.form.querySelectorAll('[data-request-field]').forEach((input) => { data[input.name] = input.value.trim(); });
      data.email = fixEmail(data.email);
      if (data.first_name) data.name = `${data.first_name} ${data.last_name}`.trim();
      this.errorEl.hidden = true;
      this.submitBtn.setAttribute('aria-busy', 'true');
      try {
        const res = await postJSON(`${apiBase(this.dataset.api)}${PATHS[this.kind]}`, data);
        if (!res.ok) throw new Error(String(res.status));
      } catch (err) {
        this.errorEl.textContent = this.t.error;
        this.errorEl.hidden = false;
        return;
      } finally {
        this.submitBtn.removeAttribute('aria-busy');
      }

      if (this.kind === 'swatch') {
        try { localStorage.setItem(LOCK_KEY, String(Date.now())); } catch (err) { /* ignore */ }
      }
      this.record(data);
      track('aura_request_sent', { form: this.kind });
      this.showSent();
    }

    /* The Marketing API's record of the person; its id stays on the device as `mkt` */
    record(data) {
      const marketing = apiBase(this.dataset.marketing);
      if (!marketing) return;
      postJSON(`${marketing}/events/user`, { name: data.name, email: data.email, phone: data.tel })
        .then((r) => r.json())
        .then((res) => {
          if (res && res.id) localStorage.setItem('mkt', res.id);
          if (this.kind === 'academy') postJSON(`${marketing}/events/classSignup`, { email: data.email }).catch(() => {});
        })
        .catch(() => {});
    }

    showSent() {
      this.querySelector('[data-request-form-view]').hidden = true;
      this.querySelector('[data-request-sent]').hidden = false;
      this.querySelector('[data-sent-title]').focus();
    }
  }

  if (!customElements.get('aura-request-form')) customElements.define('aura-request-form', AuraRequestForm);
})();
